/* eslint-disable @typescript-eslint/indent */
import React, { useMemo } from 'react';
import { Location } from '@components/Hotels/types';
import Places from '@components/Places';
import EducationalModuleBanner from '@components/EducationalModule/EducationalModuleBanner';
import useEducationalModule from '@hooks/useEducationalModule';
import { AMPLITUDE_EXPERIMENT_VARIANT_NAME } from '@constants/amplitudeExperiments';

type Props = {
  data: any;
  selectedLocation: Location | null;
  clearFilters: () => void;
  isFirstQuery: boolean;
  isLoading: boolean;
  filterParams: string[][] | undefined;
};

export default function ResultList({
  data,
  selectedLocation,
  isFirstQuery,
  isLoading,
  clearFilters,
  filterParams,
}: Props) {
  const { educationalModuleExperiment } = useEducationalModule();

  const selectedVibes: Array<string> = useMemo(() => {
    if (!filterParams) return [];
    return filterParams.reduce((ret: string[], currentFilter: string[]) => {
      // look for vibe filters only
      const vibeFilters = currentFilter.filter((str) => str.indexOf('vibes') === 0);
      if (!vibeFilters.length) return ret;

      // process vibes
      const vibes = vibeFilters.map((str) => str.split(':')[1]);
      ret.push(...vibes);
      return ret;
    }, []);
  }, [filterParams]);

  const hotelsToShow = useMemo(() => {
    if (data?.hotels) {
      if (isFirstQuery && data.hotels.length > 30) {
        return data.hotels.slice(0, 30);
      }
      return data.hotels;
    }
    return [];
  }, [isFirstQuery, data]);

  return (
    <>
      {data.hotels && data.hotels.length > 0 && (
        <div>
          {(selectedLocation?.type === 'city' || selectedLocation?.type === 'alias') &&
            !data.hotels[0].isNearBy && (
              <h1 className="pt-3 px-6 text-23 font-rp-pn-semi-bold mb-3 text-rp-primary-black w-11/12 d:text-28 d:w-full d:max-w-1024 d:px-0">
                {`Hotels in and around ${
                  selectedLocation && selectedLocation.name
                    ? selectedLocation.name
                    : data.hotels[0].cityName
                }`}
              </h1>
            )}

          <div className="flex flex-col space-y-6">
            {educationalModuleExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
              <div
                className={`px-6 d:px-0 ${
                  selectedLocation?.type === 'state' ||
                  selectedLocation?.type === 'country' ||
                  !selectedLocation
                    ? 'my-3'
                    : ''
                }`}
              >
                <EducationalModuleBanner />
              </div>
            ) : null}

            <div className="flex flex-col space-y-6 mt-3">
              <Places
                hotels={hotelsToShow}
                selectedLocation={selectedLocation}
                selectedVibes={selectedVibes}
              />
            </div>
          </div>
        </div>
      )}

      {!isLoading && data.hotels.length === 0 ? (
        <div className="w-full mx-auto flex flex-col items-center px-6 d:w-1/2 d:mt-10 d:px-0">
          <p className="font-rp-pn-semi-bold text-2xl mx-auto text-rp-primary-black pt-10 d:text-28">
            No matches found
          </p>
          <p className="font-rp-pn-regular text-base mx-auto text-rp-primary-black text-center mt-2">
            Please try another search or try reseting your filters.
          </p>
          <button
            type="button"
            onClick={clearFilters}
            id="reset-filters-button"
            className="text-rp-primary-dark bg-transparent border-rp-primary-dark text-17 border-solid border rounded-xl py-3 px-7 w-full font-rp-pn-semi-bold leading-4 cursor-pointer tracking-0.3 mt-4 mx-auto d:w-auto"
          >
            Reset Filters
          </button>
        </div>
      ) : null}
    </>
  );
}
